import { Add, CloudUpload, Delete, DirectionsBoat } from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { VesselsDataGrid } from '../../components/DataGrids/VesselsDataGrid';
import { VesselService } from '../../services/vesselService';
import { UserService, type UserInfo } from '../../services/userService';
import { useUserScope } from '../../hooks/useUserScope';
import ConfirmationPopover from '../../components/ConfirmationPopover/ConfirmationPopover';
import { useConfirmationPopover } from '../../hooks/useConfirmationPopover';
import { ToastService } from '../../services/toastService';
import { VESSEL_IMAGE_ACCEPT, validateVesselImage } from '../../utils/imageValidation';

import type { Vessel, VesselRegistration } from '@/types';

export const VesselsPage = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingVessel, setEditingVessel] = useState<Vessel | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [vesselToDelete, setVesselToDelete] = useState<Vessel | null>(null);
  const [editImageUrl, setEditImageUrl] = useState<string | undefined>(undefined);
  const [editImagePath, setEditImagePath] = useState<string | undefined>(undefined);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Confirmation popover for delete action
  const { anchorEl, isOpen, openConfirmation, closeConfirmation } = useConfirmationPopover();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<VesselRegistration>({
    defaultValues: {
      VesselName: '',
      IMO: '',
    },
  });

  const {
    register: editRegister,
    handleSubmit: editHandleSubmit,
    reset: editReset,
    control: editControl,
    formState: { errors: editErrors },
  } = useForm<{
    VesselName: string;
    RegistrationStatus: string;
    EuaPosition: number;
    FleetManager: string;
  }>({
    defaultValues: {
      VesselName: '',
      RegistrationStatus: 'APPROVED',
      EuaPosition: 0,
      FleetManager: '',
    },
  });

  const { effectiveUserId } = useUserScope();
  // Admin-only app: effectiveUserId null => all vessels
  const scopeKey = effectiveUserId ?? 'ALL';

  // Fetch vessels with effectiveUserId in key and param
  const {
    data: vessels = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['vessels', { userId: scopeKey }],
    queryFn: () => VesselService.getVessels(effectiveUserId),
    enabled: true,
  });

  // Handle query errors with toast notifications
  useEffect(() => {
    if (isError && error) {
      console.error('Failed to load vessels:', error);
      ToastService.handleApiError(error, 'Loading Vessels');
    }
  }, [isError, error]);

  // Manage the object URL lifecycle for the edit-dialog image preview.
  useEffect(() => {
    if (!selectedImageFile) {
      setImagePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedImageFile);
    setImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedImageFile]);

  // Fetch users for FleetManager dropdown
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => UserService.getAllUsers(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Create sorted user options for the Autocomplete
  const userOptions = useMemo(() => {
    const users = usersData?.users ?? [];
    return [...users].sort((a, b) => {
      const aName = a.displayName || a.email;
      const bName = b.displayName || b.email;
      return aName.localeCompare(bName);
    });
  }, [usersData?.users]);

  // Create vessel mutation
  const createVesselMutation = useMutation({
    mutationFn: (payload: VesselRegistration) =>
      VesselService.createVessel(payload, effectiveUserId),
    onSuccess: () => {
      // Invalidate vessel queries for all user scopes
      queryClient.invalidateQueries({ queryKey: ['vessels'] });
      // Invalidate voyage queries since voyages display vessel information
      queryClient.invalidateQueries({ queryKey: ['voyages'] });
      // Invalidate dashboard stats since it shows vessel counts
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setIsCreateDialogOpen(false);
      reset();
      ToastService.success('Vessel created successfully', 'Vessel Created');
    },
    onError: (error: unknown) => {
      console.error('Failed to create vessel:', error);
      ToastService.handleApiError(error, 'Vessel Creation');
    },
  });

  const deleteVesselMutation = useMutation({
    mutationFn: (vesselId: string) => VesselService.deleteVessel(vesselId, effectiveUserId),
    onSuccess: () => {
      // Invalidate vessel queries for all user scopes
      queryClient.invalidateQueries({ queryKey: ['vessels'] });
      // Invalidate voyage queries since voyages display vessel information
      queryClient.invalidateQueries({ queryKey: ['voyages'] });
      // Invalidate dashboard stats since it shows vessel counts
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      ToastService.success('Vessel deleted successfully', 'Vessel Deleted');
    },
    onError: (error: unknown) => {
      console.error('Failed to delete vessel:', error);
      ToastService.handleApiError(error, 'Vessel Deletion');
    },
  });

  const updateVesselMutation = useMutation({
    mutationFn: (data: { id: string; updates: Partial<Vessel> }) =>
      VesselService.updateVessel(data.id, data.updates, effectiveUserId),
    onSuccess: () => {
      // Invalidate vessel queries for all user scopes
      queryClient.invalidateQueries({ queryKey: ['vessels'] });
      // Invalidate voyage queries since voyages display vessel information
      queryClient.invalidateQueries({ queryKey: ['voyages'] });
      // Invalidate dashboard stats since it shows vessel counts
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      ToastService.success('Vessel updated successfully', 'Vessel Updated');
      setIsEditDialogOpen(false);
      setEditingVessel(null);
      editReset();
    },
    onError: (error: unknown) => {
      console.error('Failed to update vessel:', error);
      ToastService.handleApiError(error, 'Vessel Update');
    },
  });

  const uploadImageMutation = useMutation({
    mutationFn: (data: { vesselId: string; file: File }) =>
      VesselService.uploadVesselImage(data.vesselId, data.file, effectiveUserId),
    onSuccess: (result) => {
      setEditImageUrl(result.ImageURL);
      setEditImagePath(result.ImagePath);
      setSelectedImageFile(null);
      setImageError(null);
      queryClient.invalidateQueries({ queryKey: ['vessels'] });
      ToastService.success('Vessel image updated', 'Image Updated');
    },
    onError: (error: unknown) => {
      console.error('Failed to upload vessel image:', error);
      ToastService.handleApiError(error, 'Image Upload');
    },
  });

  const deleteImageMutation = useMutation({
    mutationFn: (vesselId: string) => VesselService.deleteVesselImage(vesselId, effectiveUserId),
    onSuccess: () => {
      setEditImageUrl(undefined);
      setEditImagePath(undefined);
      setSelectedImageFile(null);
      setImageError(null);
      queryClient.invalidateQueries({ queryKey: ['vessels'] });
      ToastService.success('Vessel image removed', 'Image Removed');
    },
    onError: (error: unknown) => {
      console.error('Failed to remove vessel image:', error);
      ToastService.handleApiError(error, 'Image Removal');
    },
  });

  const handleCreateVessel = useCallback(
    (data: VesselRegistration) => {
      createVesselMutation.mutate(data);
    },
    [createVesselMutation]
  );

  const handleEdit = useCallback(
    (vessel: Vessel) => {
      setEditingVessel(vessel);
      setEditImageUrl(vessel.ImageURL);
      setEditImagePath(vessel.ImagePath);
      setSelectedImageFile(null);
      setImageError(null);
      editReset({
        VesselName: vessel.VesselName || '',
        RegistrationStatus: vessel.RegistrationStatus || 'APPROVED',
        EuaPosition: vessel.EuaPosition || 0,
        FleetManager: vessel.FleetManager || '',
      });
      setIsEditDialogOpen(true);
    },
    [editReset]
  );

  const handleSelectImage = useCallback((file: File) => {
    const result = validateVesselImage(file);
    if (!result.ok) {
      setImageError(result.error ?? 'Invalid image file.');
      setSelectedImageFile(null);
      return;
    }
    setImageError(null);
    setSelectedImageFile(file);
  }, []);

  const handleUploadImage = useCallback(() => {
    if (!editingVessel || !selectedImageFile) return;
    uploadImageMutation.mutate({ vesselId: editingVessel.ID, file: selectedImageFile });
  }, [editingVessel, selectedImageFile, uploadImageMutation]);

  const handleRemoveImage = useCallback(() => {
    if (!editingVessel) return;
    deleteImageMutation.mutate(editingVessel.ID);
  }, [editingVessel, deleteImageMutation]);

  const handleDelete = useCallback(
    (vessel: Vessel, event?: React.MouseEvent<HTMLElement>) => {
      if (event && event.currentTarget) {
        setVesselToDelete(vessel);
        openConfirmation(event.currentTarget);
      }
    },
    [openConfirmation]
  );

  const confirmDelete = useCallback(() => {
    if (vesselToDelete) {
      deleteVesselMutation.mutate(vesselToDelete.ID);
      setVesselToDelete(null);
    }
    closeConfirmation();
  }, [vesselToDelete, deleteVesselMutation, closeConfirmation]);

  const handleEditVessel = useCallback(
    (data: {
      VesselName: string;
      RegistrationStatus: string;
      EuaPosition: number;
      FleetManager: string;
    }) => {
      if (!editingVessel) return;
      updateVesselMutation.mutate({
        id: editingVessel.ID,
        updates: data,
      });
    },
    [editingVessel, updateVesselMutation]
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Vessels Management</Typography>
        <Box display="flex" gap={2} alignItems="center">
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setIsCreateDialogOpen(true)}
            disabled={!effectiveUserId} // still require selection to know which user owns new vessel
          >
            Add Vessel
          </Button>
        </Box>
      </Box>

      <VesselsDataGrid
        vessels={vessels}
        loading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Create Vessel Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit(handleCreateVessel)}>
          <DialogTitle>Add New Vessel</DialogTitle>
          <DialogContent>
            <TextField
              {...register('VesselName', { required: 'Vessel name is required' })}
              label="Vessel Name"
              fullWidth
              margin="normal"
              error={!!errors.VesselName}
              helperText={errors.VesselName?.message}
            />
            <TextField
              {...register('IMO', {
                required: 'IMO is required',
                pattern: {
                  value: /^\d{7}$/,
                  message: 'IMO must be 7 digits',
                },
              })}
              label="IMO Number"
              fullWidth
              margin="normal"
              error={!!errors.IMO}
              helperText={errors.IMO?.message}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={createVesselMutation.isPending}>
              {createVesselMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Vessel Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={editHandleSubmit(handleEditVessel)}>
          <DialogTitle>Edit Vessel</DialogTitle>
          <DialogContent>
            {/* Vessel image */}
            <Box sx={{ mb: 1, mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Vessel Image
              </Typography>
              {imageError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {imageError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  variant="rounded"
                  src={imagePreviewUrl ?? editImageUrl}
                  alt={editingVessel?.VesselName}
                  sx={{ width: 96, height: 96, bgcolor: 'grey.200' }}
                >
                  <DirectionsBoat sx={{ color: 'grey.400' }} />
                </Avatar>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      disabled={uploadImageMutation.isPending || deleteImageMutation.isPending}
                    >
                      Choose image
                      <input
                        type="file"
                        hidden
                        accept={VESSEL_IMAGE_ACCEPT}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleSelectImage(file);
                          e.target.value = '';
                        }}
                      />
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={
                        uploadImageMutation.isPending ? (
                          <CircularProgress size={16} />
                        ) : (
                          <CloudUpload />
                        )
                      }
                      onClick={handleUploadImage}
                      disabled={!selectedImageFile || uploadImageMutation.isPending}
                    >
                      Upload
                    </Button>
                    {editImagePath && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={
                          deleteImageMutation.isPending ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Delete />
                          )
                        }
                        onClick={handleRemoveImage}
                        disabled={deleteImageMutation.isPending}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    JPG, PNG or WEBP. Max 5 MB.
                    {selectedImageFile ? ` Selected: ${selectedImageFile.name}` : ''}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <TextField
              {...editRegister('VesselName', { required: 'Vessel name is required' })}
              label="Vessel Name"
              fullWidth
              margin="normal"
              error={!!editErrors.VesselName}
              helperText={editErrors.VesselName?.message}
            />
            <Controller
              name="RegistrationStatus"
              control={editControl}
              rules={{ required: 'Status is required' }}
              render={({ field }) => (
                <FormControl fullWidth margin="normal" error={!!editErrors.RegistrationStatus}>
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select {...field} labelId="status-label" label="Status">
                    <MenuItem value="APPROVED">APPROVED</MenuItem>
                    <MenuItem value="PENDING">PENDING</MenuItem>
                    <MenuItem value="REJECTED">REJECTED</MenuItem>
                  </Select>
                  {editErrors.RegistrationStatus && (
                    <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                      {editErrors.RegistrationStatus.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
            <TextField
              {...editRegister('EuaPosition', {
                required: 'EUA Position is required',
                min: { value: 0, message: 'EUA Position must be non-negative' },
              })}
              label="EUA Position"
              type="number"
              fullWidth
              margin="normal"
              error={!!editErrors.EuaPosition}
              helperText={editErrors.EuaPosition?.message}
            />
            <Controller
              name="FleetManager"
              control={editControl}
              rules={{
                required: 'Fleet Manager is required',
                validate: (value) => value.trim() !== '' || 'Fleet Manager is required',
              }}
              render={({ field }) => (
                <Autocomplete
                  options={userOptions}
                  getOptionLabel={(option: UserInfo) => option.displayName || option.email}
                  value={userOptions.find((u) => u.uid === field.value) || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.uid || '');
                  }}
                  isOptionEqualToValue={(option, value) => option.uid === value.uid}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Fleet Manager (Owner)"
                      margin="normal"
                      error={!!editErrors.FleetManager}
                      helperText={editErrors.FleetManager?.message}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <li key={key} {...otherProps}>
                        <Box>
                          <Typography variant="body1">
                            {option.displayName || option.email}
                          </Typography>
                          {option.displayName && (
                            <Typography variant="caption" color="text.secondary">
                              {option.email}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    );
                  }}
                  fullWidth
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={updateVesselMutation.isPending}>
              {updateVesselMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Popover */}
      <ConfirmationPopover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closeConfirmation}
        onConfirm={confirmDelete}
        title="Delete Vessel"
        message={`Are you sure you want to delete vessel "${vesselToDelete?.VesselName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="warning"
        isLoading={deleteVesselMutation.isPending}
      />
    </Box>
  );
};
